// Persistence: IndexedDB for records, localStorage for scalars.
//
// The `records` store is generic on purpose — one object store with an id, a
// payload, and bookkeeping fields. Whatever your app saves goes in `data`.
// `dirty` and `updatedAt` exist so the sync addon can bolt on later without
// migrating anything.

const DB_NAME = 'app';
const DB_VERSION = 1;
const STORE = 'records';

let dbPromise;

function open() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'id' });
        os.createIndex('updatedAt', 'updatedAt');
        os.createIndex('dirty', 'dirty');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(mode, fn) {
  return open().then(
    (db) =>
      new Promise((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const store = t.objectStore(STORE);
        let result;
        try { result = fn(store); } catch (e) { reject(e); return; }
        t.oncomplete = () => resolve(result?.result ?? result);
        t.onerror = () => reject(t.error);
      }),
  );
}

export const records = {
  put(record) {
    const now = Date.now();
    return tx('readwrite', (s) =>
      s.put({ dirty: 1, createdAt: now, ...record, updatedAt: now }),
    );
  },
  get(id) { return tx('readonly', (s) => s.get(id)); },
  all() { return tx('readonly', (s) => s.getAll()); },
  delete(id) { return tx('readwrite', (s) => s.delete(id)); },
  count() { return tx('readonly', (s) => s.count()); },
  /** Records the sync addon has not yet pushed. */
  dirty() { return tx('readonly', (s) => s.index('dirty').getAll(1)); },
};

// --- preferences ------------------------------------------------------------
// A Proxy over localStorage so callers read and write plain properties.
// Values are JSON, so booleans survive the round trip as booleans.

const PREFIX = 'pref:';

export const prefs = new Proxy(
  {},
  {
    get(_, key) {
      try {
        const raw = localStorage.getItem(PREFIX + String(key));
        return raw === null ? undefined : JSON.parse(raw);
      } catch { return undefined; }
    },
    set(_, key, value) {
      try { localStorage.setItem(PREFIX + String(key), JSON.stringify(value)); } catch { /* private mode */ }
      return true;
    },
    deleteProperty(_, key) {
      try { localStorage.removeItem(PREFIX + String(key)); } catch { /* ignore */ }
      return true;
    },
  },
);

/** Apply saved preferences at boot, filling defaults for anything unset. */
export function hydrateFlags(defaults = { sound: true, haptics: true }) {
  for (const [k, v] of Object.entries(defaults)) {
    if (prefs[k] === undefined) prefs[k] = v;
  }
  return { ...defaults, ...Object.fromEntries(Object.keys(defaults).map((k) => [k, prefs[k]])) };
}
